import { img } from "../images";
import type { Blog } from "./types";

const DR_ARUN = "Dr. M. V. Arun Vijaiyan";
const ARUN_BIO =
  "Dr. M. V. Arun Vijaiyan is a Urologist and Andrologist at AV Clinic, Krishnagiri, with an M.S. and M.Ch. (Uro). He focuses on kidney stone care, urinary and prostate health, and male reproductive and sexual health, with a patient-centred approach to diagnosis and treatment.";
const ARUN_ROLE = "Urologist & Andrologist at AV Clinic, Krishnagiri";

export const andrologyBlogs: Blog[] = [
  {
    slug: "erectile-dysfunction-causes",
    title: "Common Causes of Erectile Dysfunction",
    category: "Sexual Health",
    excerpt:
      "Erectile dysfunction is common and treatable. Understand the physical and psychological factors that can contribute.",
    image: img.svcErectileDysfunction,
    imageAlt: "Common causes of erectile dysfunction",
    author: DR_ARUN,
    authorBio: ARUN_BIO,
    authorRole: ARUN_ROLE,
    date: "2026-09-01",
    modified: "2026-09-01",
    readTime: "5 min read",
    popular: true,
    tags: ["erectile dysfunction", "sexual health", "men's health", "andrology"],
    primaryKeyword: "causes of erectile dysfunction",
    secondaryKeywords: ["erectile dysfunction causes", "ED treatment", "male sexual health"],
    relatedService: "erectile-dysfunction",
    relatedServiceLabel: "Erectile Dysfunction Care",
    relatedArticles: ["erectile-dysfunction-treatment", "male-sexual-health-lifestyle", "low-testosterone"],
    sections: [
      {
        heading: "Erectile dysfunction is more common than you think",
        body: [
          "Erectile dysfunction (ED) is the difficulty in achieving or maintaining an erection firm enough for intercourse. It affects many men at some point and is often treatable, so it is worth discussing openly.",
        ],
      },
      {
        heading: "Physical causes",
        body: ["Physical factors are often behind ED:"],
        list: [
          "Conditions affecting blood flow, such as diabetes or high blood pressure",
          "Heart and vascular disease",
          "Hormonal imbalances, including low testosterone",
          "Side effects of certain medications",
          "Smoking and excess alcohol",
        ],
      },
      {
        heading: "Psychological factors",
        body: [
          "Stress, anxiety, depression, relationship concerns and performance anxiety can all contribute to ED. Often several factors combine.",
        ],
      },
      {
        heading: "Why evaluation matters",
        body: [
          "Because ED can be a sign of an underlying health problem, a proper assessment matters. An andrologist can identify root causes and guide appropriate care.",
        ],
      },
    ],
    faqs: [
      {
        q: "Is erectile dysfunction a normal part of ageing?",
        a: "While it can be more common with age, ED is not something you must simply accept. Many causes are treatable.",
      },
      {
        q: "Can stress cause erectile dysfunction?",
        a: "Yes. Stress and anxiety can significantly contribute to ED, sometimes alongside physical factors.",
      },
    ],
    seo: {
      title: "Causes of Erectile Dysfunction | AV Clinic",
      description:
        "Understand the common causes of erectile dysfunction — physical and psychological factors — and how to get help. Andrology care at AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "erectile-dysfunction-treatment",
    title: "Erectile Dysfunction Treatment Options",
    category: "Sexual Health",
    excerpt:
      "Erectile dysfunction can often be effectively managed. Explore the treatment options available and how to choose one.",
    image: img.svcErectileDysfunction,
    imageAlt: "Erectile dysfunction treatment options",
    author: DR_ARUN,
    authorBio: ARUN_BIO,
    authorRole: ARUN_ROLE,
    date: "2026-08-28",
    modified: "2026-08-28",
    readTime: "6 min read",
    tags: ["erectile dysfunction", "ED treatment", "sexual health", "andrology"],
    primaryKeyword: "erectile dysfunction treatment",
    secondaryKeywords: ["ED treatment", "erectile dysfunction treatment Krishnagiri", "male sexual health treatment"],
    relatedService: "erectile-dysfunction",
    relatedServiceLabel: "Erectile Dysfunction Care",
    relatedArticles: ["erectile-dysfunction-causes", "male-sexual-health-lifestyle", "when-to-see-andrologist"],
    sections: [
      {
        heading: "Treatment starts with the cause",
        body: [
          "The right treatment for erectile dysfunction depends on its underlying cause. This is why a proper assessment — rather than self-treatment — is the safest starting point.",
        ],
      },
      {
        heading: "Lifestyle and first-line approaches",
        body: ["Depending on the cause, care may begin with:"],
        list: [
          "Improving diet, exercise and weight management",
          "Managing stress and sleep",
          "Addressing underlying conditions such as diabetes or blood pressure",
          "Reducing alcohol and stopping smoking",
        ],
      },
      {
        heading: "Medical options",
        body: [
          "Where indicated, an andrologist may recommend medication, hormonal assessment or other specialist approaches. Every option is discussed with you so you understand the benefits and considerations.",
        ],
      },
      {
        heading: "Counselling and support",
        body: [
          "For psychological factors, counselling or sex therapy can be highly effective, often combined with medical care. A supportive, open conversation is key.",
        ],
      },
    ],
    faqs: [
      {
        q: "Can erectile dysfunction be cured?",
        a: "Many cases can be significantly improved or resolved by treating the underlying cause. A tailored plan gives the best results.",
      },
      {
        q: "Should I see a doctor for erectile dysfunction?",
        a: "Yes. A consultation is recommended, both to manage the concern and to check for underlying health issues.",
      },
    ],
    seo: {
      title: "Erectile Dysfunction Treatment Options | AV Clinic",
      description:
        "Explore erectile dysfunction treatment options in Krishnagiri — lifestyle changes, medication and specialist care. Andrology support at AV Clinic.",
    },
  },
  {
    slug: "premature-ejaculation-causes",
    title: "Causes of Premature Ejaculation",
    category: "Sexual Health",
    excerpt:
      "Premature ejaculation is a common concern. Understand the factors that can contribute to it.",
    image: img.svcPrematureEjaculation,
    imageAlt: "Causes of premature ejaculation",
    author: DR_ARUN,
    authorBio: ARUN_BIO,
    authorRole: ARUN_ROLE,
    date: "2026-08-24",
    modified: "2026-08-24",
    readTime: "5 min read",
    tags: ["premature ejaculation", "sexual health", "men's health", "andrology"],
    primaryKeyword: "premature ejaculation causes",
    secondaryKeywords: ["premature ejaculation", "PE treatment", "male sexual health"],
    relatedService: "premature-ejaculation",
    relatedServiceLabel: "Premature Ejaculation Care",
    relatedArticles: ["premature-ejaculation-treatment", "male-sexual-health-lifestyle", "when-to-see-andrologist"],
    sections: [
      {
        heading: "What premature ejaculation is",
        body: [
          "Premature ejaculation is ejaculation that happens sooner than desired, often with little control. It is a very common concern and can affect men of any age.",
        ],
      },
      {
        heading: "Contributing factors",
        body: ["Several factors can contribute:"],
        list: [
          "Performance anxiety or stress",
          "Heightened sensitivity",
          "Hormonal or neurological factors",
          "Relationship concerns",
          "In some cases, underlying health conditions",
        ],
      },
      {
        heading: "It is not something to be embarrassed about",
        body: [
          "Premature ejaculation is common and treatable. Many men find that discussing it openly with a specialist reduces anxiety and helps find an effective approach.",
        ],
      },
      {
        heading: "Getting help",
        body: [
          "An andrologist can help identify contributing factors and guide management, which may include behavioural techniques, counselling and, where suitable, medication.",
        ],
      },
    ],
    faqs: [
      {
        q: "Is premature ejaculation curable?",
        a: "It can often be significantly improved with the right approach. Many men benefit from behavioural and psychological techniques plus medical support.",
      },
      {
        q: "Can stress cause premature ejaculation?",
        a: "Yes. Anxiety and stress are common contributing factors and are often part of the picture.",
      },
    ],
    seo: {
      title: "Causes of Premature Ejaculation | AV Clinic",
      description:
        "Understand the causes of premature ejaculation and how to get confidential help. Sexual health and andrology care at AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "premature-ejaculation-treatment",
    title: "Premature Ejaculation Treatment",
    category: "Sexual Health",
    excerpt:
      "Premature ejaculation can be managed effectively. Learn about the approaches available.",
    image: img.svcPrematureEjaculation,
    imageAlt: "Premature ejaculation treatment options",
    author: DR_ARUN,
    authorBio: ARUN_BIO,
    authorRole: ARUN_ROLE,
    date: "2026-08-20",
    modified: "2026-08-20",
    readTime: "5 min read",
    tags: ["premature ejaculation", "PE treatment", "sexual health", "andrology"],
    primaryKeyword: "premature ejaculation treatment",
    secondaryKeywords: ["premature ejaculation treatment Krishnagiri", "PE cure", "ejaculation control"],
    relatedService: "premature-ejaculation",
    relatedServiceLabel: "Premature Ejaculation Care",
    relatedArticles: ["premature-ejaculation-causes", "male-sexual-health-lifestyle", "when-to-see-andrologist"],
    sections: [
      {
        heading: "A tailored approach works best",
        body: [
          "Premature ejaculation is best managed with a plan suited to you. There is no one-size-fits-all answer, but many men see meaningful improvement.",
        ],
      },
      {
        heading: "Behavioural and psychological techniques",
        body: ["These are often the first-line approaches:"],
        list: [
          "Techniques to improve control",
          "Relaxation and stress-reduction strategies",
          "Counselling to address anxiety or relationship factors",
        ],
      },
      {
        heading: "Medical options",
        body: [
          "In selected cases, an andrologist may consider medication or other specialist approaches to help delay ejaculation. These are prescribed and discussed carefully with you.",
        ],
      },
      {
        heading: "The value of a consultation",
        body: [
          "A confidential discussion helps identify what is contributing and rule out any underlying health problem. It is a positive, practical step forward.",
        ],
      },
    ],
    faqs: [
      {
        q: "How long does treatment take to work?",
        a: "This varies. Some men notice improvement with behavioural techniques quickly, while others need a combination of approaches over several weeks.",
      },
      {
        q: "Is medication always needed?",
        a: "No. Many men improve with behavioural and psychological techniques alone. Medication is considered when appropriate.",
      },
    ],
    seo: {
      title: "Premature Ejaculation Treatment Options | AV Clinic",
      description:
        "Learn about premature ejaculation treatment options in Krishnagiri — techniques, counselling and medication. Confidential andrology care at AV Clinic.",
    },
  },
  {
    slug: "male-sexual-health-lifestyle",
    title: "Male Sexual Health and Lifestyle",
    category: "Sexual Health",
    excerpt:
      "Your daily habits influence your sexual and reproductive health. Learn how lifestyle supports male wellbeing.",
    image: img.svcMaleSexualHealth,
    imageAlt: "Male sexual health and lifestyle habits",
    author: DR_ARUN,
    authorBio: ARUN_BIO,
    authorRole: ARUN_ROLE,
    date: "2026-08-16",
    modified: "2026-08-16",
    readTime: "5 min read",
    tags: ["sexual health", "lifestyle", "men's health", "andrology"],
    primaryKeyword: "male sexual health",
    secondaryKeywords: ["male sexual health tips", "lifestyle and sexual health", "men health"],
    relatedService: "male-sexual-health",
    relatedServiceLabel: "Male Sexual Health",
    relatedArticles: ["erectile-dysfunction-causes", "low-testosterone", "exercise-mens-health"],
    sections: [
      {
        heading: "Lifestyle shapes sexual health",
        body: [
          "Sexual and reproductive health is closely linked to overall wellbeing. Simple lifestyle choices can make a meaningful difference.",
        ],
      },
      {
        heading: "Habits that support male health",
        body: ["These are worth adopting:"],
        list: [
          "Regular physical activity and a healthy weight",
          "A balanced diet low in excess sugar and processed food",
          "Managing stress and getting adequate sleep",
          "Limiting alcohol and avoiding smoking",
          "Managing conditions such as diabetes and blood pressure",
        ],
      },
      {
        heading: "The heart–sexual health link",
        body: [
          "Good cardiovascular health supports healthy erections. Protecting your heart through exercise and diet protects male sexual health too.",
        ],
      },
      {
        heading: "When to seek advice",
        body: [
          "If you notice persistent changes in sexual function, libido or fertility, a confidential consultation can help identify and address the cause early.",
        ],
      },
    ],
    faqs: [
      {
        q: "Does exercise improve sexual health?",
        a: "Regular activity improves blood flow and cardiovascular health, which can support erectile function and libido.",
      },
      {
        q: "Can diet affect sexual health?",
        a: "Yes. A balanced diet supports energy, heart health and hormonal balance, all of which contribute to sexual wellbeing.",
      },
    ],
    seo: {
      title: "Male Sexual Health & Lifestyle Habits | AV Clinic",
      description:
        "Learn how lifestyle supports male sexual and reproductive health — exercise, diet, stress and sleep. Andrology guidance at AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "low-testosterone",
    title: "Low Testosterone: Symptoms and Causes",
    category: "Men's Health",
    excerpt:
      "Testosterone levels naturally decline with age, but low levels can cause noticeable symptoms. Understand the signs and causes.",
    image: img.svcMaleHormonal,
    imageAlt: "Low testosterone symptoms and causes",
    author: DR_ARUN,
    authorBio: ARUN_BIO,
    authorRole: ARUN_ROLE,
    date: "2026-08-12",
    modified: "2026-08-12",
    readTime: "5 min read",
    popular: true,
    tags: ["testosterone", "low T", "men's health", "hormones"],
    primaryKeyword: "low testosterone symptoms",
    secondaryKeywords: ["low testosterone", "testosterone levels", "male hormonal health"],
    relatedService: "male-hormonal-health",
    relatedServiceLabel: "Male Hormonal Health",
    relatedArticles: ["male-hormonal-health", "male-sexual-health-lifestyle", "erectile-dysfunction-causes"],
    sections: [
      {
        heading: "What testosterone does",
        body: [
          "Testosterone is the primary male sex hormone. It supports muscle mass, bone health, libido, energy and mood. Levels naturally decline with age, but a marked drop can cause symptoms.",
        ],
      },
      {
        heading: "Symptoms of low testosterone",
        body: ["Possible signs include:"],
        list: [
          "Reduced sex drive",
          "Low energy and fatigue",
          "Decreased muscle strength",
          "Mood changes or low mood",
          "Difficulty with erections",
        ],
      },
      {
        heading: "Causes",
        body: [
          "Causes include natural ageing, certain medical conditions, obesity, some medications and hormonal disorders. A proper evaluation determines whether levels are genuinely low.",
        ],
      },
      {
        heading: "Evaluation and management",
        body: [
          "If you have symptoms, a hormone assessment can confirm whether testosterone is low. Management is individualised and may involve lifestyle changes and, where appropriate, specialist input.",
        ],
      },
    ],
    faqs: [
      {
        q: "How is low testosterone diagnosed?",
        a: "A blood test measuring hormone levels, combined with your symptoms, is used to diagnose low testosterone.",
      },
      {
        q: "Does low testosterone always need treatment?",
        a: "Not necessarily. Mild changes may be managed with lifestyle. Treatment depends on symptoms and confirmed levels.",
      },
    ],
    seo: {
      title: "Low Testosterone: Symptoms & Causes | AV Clinic",
      description:
        "Learn the symptoms and causes of low testosterone and how it is evaluated. Male hormonal health care at AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "male-hormonal-health",
    title: "Male Hormonal Health",
    category: "Men's Health",
    excerpt:
      "Hormones influence energy, mood, strength and fertility. Learn how to support healthy hormone balance in men.",
    image: img.svcMaleHormonal,
    imageAlt: "Male hormonal health and balance",
    author: DR_ARUN,
    authorBio: ARUN_BIO,
    authorRole: ARUN_ROLE,
    date: "2026-08-08",
    modified: "2026-08-08",
    readTime: "5 min read",
    tags: ["hormonal health", "testosterone", "men's health", "andrology"],
    primaryKeyword: "male hormonal health",
    secondaryKeywords: ["mens hormones", "hormone balance men", "testosterone health"],
    relatedService: "male-hormonal-health",
    relatedServiceLabel: "Male Hormonal Health",
    relatedArticles: ["low-testosterone", "male-sexual-health-lifestyle", "male-infertility-causes"],
    sections: [
      {
        heading: "Hormones underpin male health",
        body: [
          "Male hormones, particularly testosterone, influence muscle, bone, energy, mood, libido and fertility. When balanced, they support overall wellbeing.",
        ],
      },
      {
        heading: "How to support hormonal health",
        body: ["Healthy habits play a key role:"],
        list: [
          "Regular exercise and resistance training",
          "Eating a balanced diet with adequate protein and healthy fats",
          "Getting quality sleep",
          "Managing stress",
          "Maintaining a healthy weight",
        ],
      },
      {
        heading: "When hormones may be off",
        body: [
          "Fatigue, reduced libido, mood changes, muscle loss and fertility concerns can all relate to hormonal imbalance. These symptoms warrant evaluation.",
        ],
      },
      {
        heading: "Seeking help",
        body: [
          "If you have concerns about your hormones, an andrology assessment can measure levels and identify any underlying cause. Management is individualised.",
        ],
      },
    ],
    faqs: [
      {
        q: "What affects male hormone levels?",
        a: "Age, lifestyle, weight, sleep, stress, medical conditions and certain medications can all influence male hormone levels.",
      },
      {
        q: "Can lifestyle improve hormone health?",
        a: "Yes. Exercise, good sleep, a balanced diet and stress management all support healthy hormone balance.",
      },
    ],
    seo: {
      title: "Male Hormonal Health & Balance | AV Clinic",
      description:
        "Understand male hormonal health — how hormones affect energy, mood and fertility, and how to support balance. Andrology care at AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "testicular-pain",
    title: "Testicular Pain: Common Causes",
    category: "Men's Health",
    excerpt:
      "Testicular pain can range from mild to severe. Learn the common causes and when it needs urgent attention.",
    image: img.svcMaleReproductive,
    imageAlt: "Testicular pain common causes",
    author: DR_ARUN,
    authorBio: ARUN_BIO,
    authorRole: ARUN_ROLE,
    date: "2026-08-04",
    modified: "2026-08-04",
    readTime: "5 min read",
    tags: ["testicular pain", "men's health", "andrology", "varicocele"],
    primaryKeyword: "testicular pain",
    secondaryKeywords: ["testicle pain", "scrotal pain", "male scrotal health"],
    relatedService: "male-reproductive-health",
    relatedServiceLabel: "Male Reproductive Health",
    relatedArticles: ["varicocele", "hydrocele", "when-to-see-andrologist"],
    sections: [
      {
        heading: "Testicular pain should never be ignored",
        body: [
          "Pain in the testicle or scrotum can be caused by several conditions. Some are minor, but others need prompt attention, so it is important to get it checked.",
        ],
      },
      {
        heading: "Common causes",
        body: ["Common causes of testicular pain include:"],
        list: [
          "Infection or inflammation",
          "Varicocele — enlarged veins in the scrotum",
          "Hydrocele — a fluid collection around the testicle",
          "Injury or trauma",
          "In some cases, an underlying condition affecting the testicle",
        ],
      },
      {
        heading: "When it is urgent",
        body: [
          "Sudden, severe testicular pain needs urgent evaluation, as it can indicate a condition that requires timely treatment. Do not delay in this situation.",
        ],
      },
      {
        heading: "Getting it assessed",
        body: [
          "A specialist evaluation, which may include an examination and ultrasound, identifies the cause. Early assessment is more comfortable and safer.",
        ],
      },
    ],
    faqs: [
      {
        q: "When should I see a doctor for testicular pain?",
        a: "Any persistent or severe pain should be evaluated. Sudden severe pain needs urgent attention.",
      },
      {
        q: "Can testicular pain be a sign of a serious condition?",
        a: "It can, though many causes are benign. A proper evaluation is the safest way to know.",
      },
    ],
    seo: {
      title: "Testicular Pain: Causes & When to Act | AV Clinic",
      description:
        "Learn the common causes of testicular pain and when to seek urgent care. Urology and andrology services at AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "varicocele",
    title: "Varicocele: Symptoms and Treatment",
    category: "Andrology",
    excerpt:
      "Varicocele is a common cause of enlarged veins in the scrotum and can affect fertility. Learn the symptoms and treatment.",
    image: img.svcVaricocele,
    imageAlt: "Varicocele symptoms and treatment",
    author: DR_ARUN,
    authorBio: ARUN_BIO,
    authorRole: ARUN_ROLE,
    date: "2026-07-30",
    modified: "2026-07-30",
    readTime: "5 min read",
    tags: ["varicocele", "testicular veins", "male infertility", "andrology"],
    primaryKeyword: "varicocele treatment",
    secondaryKeywords: ["varicocele symptoms", "varicocele", "varicocele and fertility"],
    relatedService: "varicocele",
    relatedServiceLabel: "Varicocele Management",
    relatedArticles: ["testicular-pain", "hydrocele", "male-infertility-causes"],
    sections: [
      {
        heading: "What is varicocele?",
        body: [
          "A varicocele is an enlargement of the veins within the scrotum, similar to varicose veins in the legs. It is common and usually develops on the left side.",
        ],
      },
      {
        heading: "Symptoms",
        body: ["Many men with a varicocele have no symptoms, but some notice:"],
        list: [
          "A dull ache or heaviness in the scrotum",
          "A feeling of fullness that can worsen with prolonged standing",
          "In some cases, a noticeable lump",
          "Infertility, in some men",
        ],
      },
      {
        heading: "How it affects fertility",
        body: [
          "A varicocele can raise scrotal temperature and affect sperm production, contributing to male infertility. This is a common, treatable cause of fertility issues.",
        ],
      },
      {
        heading: "Treatment",
        body: [
          "Not all varicoceles need treatment. If it causes pain or affects fertility, options may include surgical or minimally invasive repair. An andrologist will advise whether treatment is appropriate for you.",
        ],
      },
    ],
    faqs: [
      {
        q: "Does varicocele always cause symptoms?",
        a: "No. Many varicoceles cause no symptoms and are found incidentally during an exam.",
      },
      {
        q: "Can varicocele affect fertility?",
        a: "Yes. Varicocele is a common, treatable cause of male infertility. Treatment may improve sperm parameters in some men.",
      },
    ],
    seo: {
      title: "Varicocele: Symptoms & Treatment | AV Clinic",
      description:
        "Learn about varicocele — symptoms, how it can affect fertility and the treatment options. Andrology care at AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "hydrocele",
    title: "Hydrocele: Causes and Treatment",
    category: "Andrology",
    excerpt:
      "A hydrocele is a fluid collection around the testicle that causes swelling. Learn its causes and treatment.",
    image: img.svcMaleReproductive,
    imageAlt: "Hydrocele causes and treatment",
    author: DR_ARUN,
    authorBio: ARUN_BIO,
    authorRole: ARUN_ROLE,
    date: "2026-07-26",
    modified: "2026-07-26",
    readTime: "5 min read",
    tags: ["hydrocele", "scrotal swelling", "men's health", "andrology"],
    primaryKeyword: "hydrocele treatment",
    secondaryKeywords: ["hydrocele", "scrotal swelling", "hydrocele causes"],
    relatedService: "male-reproductive-health",
    relatedServiceLabel: "Male Reproductive Health",
    relatedArticles: ["varicocele", "testicular-pain", "when-to-see-andrologist"],
    sections: [
      {
        heading: "What is a hydrocele?",
        body: [
          "A hydrocele is a collection of fluid around one or both testicles, causing swelling in the scrotum. It is usually painless and is not the same as a tumour.",
        ],
      },
      {
        heading: "Causes",
        body: ["Hydroceles can be caused by:"],
        list: [
          "Fluid build-up in the membranes around the testicle",
          "Inflammation or injury",
          "Infection",
          "Occasionally, an underlying scrotal condition",
        ],
      },
      {
        heading: "Symptoms",
        body: [
          "The main symptom is a painless swelling or heaviness in the scrotum. In some cases, it may be uncomfortable or affect daily activity.",
        ],
      },
      {
        heading: "Treatment",
        body: [
          "Small hydroceles may resolve on their own. If it is large, painful or persistent, a specialist may recommend drainage or surgery. An examination and ultrasound help guide the decision.",
        ],
      },
    ],
    faqs: [
      {
        q: "Is a hydrocele serious?",
        a: "Usually not, but persistent swelling should be evaluated to confirm the cause and rule out other conditions.",
      },
      {
        q: "Can a hydrocele go away on its own?",
        a: "Some do, especially small ones. Persistent or large hydroceles may need treatment.",
      },
    ],
    seo: {
      title: "Hydrocele: Causes & Treatment | AV Clinic",
      description:
        "Learn about hydrocele, a fluid collection around the testicle — its causes and treatment. Andrology care at AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "male-infertility-causes",
    title: "Male Infertility: Causes",
    category: "Infertility",
    excerpt:
      "Male factors contribute to infertility in many couples. Learn the common causes of male infertility.",
    image: img.svcMaleInfertility,
    imageAlt: "Causes of male infertility",
    author: DR_ARUN,
    authorBio: ARUN_BIO,
    authorRole: ARUN_ROLE,
    date: "2026-07-22",
    modified: "2026-07-22",
    readTime: "5 min read",
    popular: true,
    tags: ["male infertility", "sperm count", "fertility", "andrology"],
    primaryKeyword: "male infertility causes",
    secondaryKeywords: ["male infertility", "low sperm count", "male factor infertility"],
    relatedService: "male-infertility",
    relatedServiceLabel: "Male Infertility",
    relatedArticles: ["male-infertility-treatment", "sperm-count-fertility", "when-to-see-infertility-specialist"],
    sections: [
      {
        heading: "Men contribute to infertility more often than assumed",
        body: [
          "In a significant proportion of couples trying to conceive, a male factor is involved. Understanding this is important, because many male factors are treatable.",
        ],
      },
      {
        heading: "Common causes",
        body: ["Male infertility can be caused by:"],
        list: [
          "Low sperm count or poor sperm quality",
          "Varicocele",
          "Hormonal imbalances",
          "Obstruction of the sperm pathway",
          "Lifestyle factors, medical conditions or past infections",
        ],
      },
      {
        heading: "Why evaluation matters",
        body: [
          "Because the cause is often identifiable, a proper evaluation is worthwhile. A semen analysis, along with a clinical assessment, gives clear answers.",
        ],
      },
      {
        heading: "A couple's journey",
        body: [
          "Fertility is a couple's concern. Identifying and addressing male factors early can make a real difference in planning the way forward.",
        ],
      },
    ],
    faqs: [
      {
        q: "How common is male infertility?",
        a: "Male factors contribute to infertility in a meaningful proportion of couples. It is more common than many people think.",
      },
      {
        q: "What is the first test for male infertility?",
        a: "A semen analysis is usually the first test, combined with a clinical assessment of reproductive history.",
      },
    ],
    seo: {
      title: "Male Infertility: Common Causes | AV Clinic",
      description:
        "Understand the common causes of male infertility and how they are identified. Andrology and fertility care at AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "male-infertility-treatment",
    title: "Male Infertility Treatment Options",
    category: "Infertility",
    excerpt:
      "Many causes of male infertility can be treated. Explore the options available and how care is planned.",
    image: img.svcMaleInfertility,
    imageAlt: "Male infertility treatment options",
    author: DR_ARUN,
    authorBio: ARUN_BIO,
    authorRole: ARUN_ROLE,
    date: "2026-07-18",
    modified: "2026-07-18",
    readTime: "6 min read",
    tags: ["male infertility treatment", "fertility", "sperm health", "andrology"],
    primaryKeyword: "male infertility treatment",
    secondaryKeywords: ["male infertility treatment Krishnagiri", "sperm health treatment", "fertility treatment men"],
    relatedService: "male-infertility",
    relatedServiceLabel: "Male Infertility",
    relatedArticles: ["male-infertility-causes", "improve-sperm-health", "sperm-health-lifestyle"],
    sections: [
      {
        heading: "Treatment depends on the cause",
        body: [
          "The right treatment for male infertility depends on what is causing it. A proper evaluation — including a semen analysis — is the starting point.",
        ],
      },
      {
        heading: "Lifestyle and general measures",
        body: ["For many men, these help:"],
        list: [
          "Improving diet, weight and exercise",
          "Quitting smoking and limiting alcohol",
          "Managing stress and ensuring good sleep",
          "Avoiding excess heat exposure to the testicles",
        ],
      },
      {
        heading: "Medical and surgical options",
        body: [
          "Depending on the cause, treatment may include medication for hormonal issues, treating an infection, or surgical repair of a condition such as a varicocele. Some cases may benefit from assisted reproductive options.",
        ],
      },
      {
        heading: "A shared plan",
        body: [
          "Male infertility care is best coordinated with your partner's care. A clear, shared plan improves the chance of a positive outcome and reduces stress.",
        ],
      },
    ],
    faqs: [
      {
        q: "Can male infertility be treated?",
        a: "Many causes are treatable, and treatment can significantly improve the chance of conception. Your specialist will advise what is realistic.",
      },
      {
        q: "How long does male infertility treatment take?",
        a: "This varies by cause. Sperm production takes several months, so improvements may take time. Your specialist will set expectations.",
      },
    ],
    seo: {
      title: "Male Infertility Treatment Options | AV Clinic",
      description:
        "Explore male infertility treatment options in Krishnagiri — lifestyle, medical and surgical care. Andrology and fertility support at AV Clinic.",
    },
  },
  {
    slug: "sperm-health-lifestyle",
    title: "Sperm Health and Lifestyle",
    category: "Men's Health",
    excerpt:
      "Your habits influence sperm quality and fertility. Learn how lifestyle supports healthy sperm.",
    image: img.svcSpermAnalysis,
    imageAlt: "Sperm health and lifestyle habits",
    author: DR_ARUN,
    authorBio: ARUN_BIO,
    authorRole: ARUN_ROLE,
    date: "2026-07-14",
    modified: "2026-07-14",
    readTime: "5 min read",
    tags: ["sperm health", "fertility", "lifestyle", "men's health"],
    primaryKeyword: "sperm health",
    secondaryKeywords: ["how to improve sperm health", "sperm quality", "male fertility lifestyle"],
    relatedService: "sperm-analysis",
    relatedServiceLabel: "Sperm Analysis",
    relatedArticles: ["improve-sperm-health", "male-infertility-causes", "exercise-mens-health"],
    sections: [
      {
        heading: "Habits influence sperm quality",
        body: [
          "Sperm quality — including count, movement and shape — is affected by your lifestyle and health. Making positive changes can support fertility.",
        ],
      },
      {
        heading: "What helps",
        body: ["These habits support healthy sperm:"],
        list: [
          "Eating a balanced diet with fruit, vegetables and healthy fats",
          "Maintaining a healthy weight",
          "Regular exercise",
          "Quitting smoking and reducing alcohol",
          "Getting adequate sleep and managing stress",
        ],
      },
      {
        heading: "What to avoid",
        body: [
          "Excess heat to the testicles, smoking, high alcohol intake and poor diet are linked to reduced sperm quality. Certain medications and medical conditions can also play a role.",
        ],
      },
      {
        heading: "When to check",
        body: [
          "If you are trying to conceive without success, or have concerns, a semen analysis provides a clear picture of sperm health and guides next steps.",
        ],
      },
    ],
    faqs: [
      {
        q: "How long does it take lifestyle changes to improve sperm health?",
        a: "Sperm production takes about two to three months, so improvements from lifestyle changes may become visible in a few months.",
      },
      {
        q: "Does smoking affect sperm quality?",
        a: "Yes. Smoking is linked to lower sperm count and quality. Quitting can improve fertility.",
      },
    ],
    seo: {
      title: "Sperm Health & Lifestyle | AV Clinic",
      description:
        "Learn how lifestyle affects sperm health and fertility — diet, exercise, sleep and habits. Andrology guidance at AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "improve-sperm-health",
    title: "How to Improve Sperm Health Naturally",
    category: "Andrology",
    excerpt:
      "Small lifestyle changes can support healthier sperm. Learn practical, evidence-informed ways to improve sperm health.",
    image: img.svcSpermAnalysis,
    imageAlt: "How to improve sperm health naturally",
    author: DR_ARUN,
    authorBio: ARUN_BIO,
    authorRole: ARUN_ROLE,
    date: "2026-07-10",
    modified: "2026-07-10",
    readTime: "5 min read",
    tags: ["sperm health", "fertility", "diet", "natural remedies"],
    primaryKeyword: "improve sperm health",
    secondaryKeywords: ["improve sperm health naturally", "sperm count increase", "male fertility tips"],
    relatedService: "sperm-analysis",
    relatedServiceLabel: "Sperm Analysis",
    relatedArticles: ["sperm-health-lifestyle", "male-infertility-treatment", "healthy-diet-skin"],
    sections: [
      {
        heading: "Everyday steps that support fertility",
        body: [
          "Improving sperm health is largely about lifestyle. While no change guarantees a specific outcome, these steps are linked to better sperm quality.",
        ],
      },
      {
        heading: "Focus on these habits",
        body: ["Consider these practical steps:"],
        list: [
          "Stay at a healthy weight and exercise regularly",
          "Eat a diet rich in vegetables, fruit, whole grains and healthy fats",
          "Quit smoking and limit alcohol",
          "Manage stress and prioritise sleep",
          "Avoid overheating the testicles, such as with prolonged hot baths",
        ],
      },
      {
        heading: "Timing matters",
        body: [
          "Because sperm production takes a few months, make changes early and give them time. Consistency is more important than quick fixes.",
        ],
      },
      {
        heading: "Seek a clear picture",
        body: [
          "A semen analysis tells you where you stand and whether further evaluation is needed. It is a simple, worthwhile first step for couples trying to conceive.",
        ],
      },
    ],
    faqs: [
      {
        q: "Can natural methods improve sperm count?",
        a: "Lifestyle changes such as a healthy diet, exercise and stopping smoking are linked to better sperm quality, but results vary. Check with a specialist for your situation.",
      },
      {
        q: "Do supplements boost sperm health?",
        a: "Some supplements are studied for sperm health, but they are not a substitute for lifestyle. Discuss any supplement with a doctor first.",
      },
    ],
    seo: {
      title: "How to Improve Sperm Health Naturally | AV Clinic",
      description:
        "Practical steps to improve sperm health naturally — diet, exercise, sleep and habits. Male fertility guidance at AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "when-to-see-andrologist",
    title: "When Should Men See an Andrologist?",
    category: "Andrology",
    excerpt:
      "An andrologist specialises in male reproductive and sexual health. Learn when a consultation is worthwhile.",
    image: img.svcAndroConsult,
    imageAlt: "When to see an andrologist",
    author: DR_ARUN,
    authorBio: ARUN_BIO,
    authorRole: ARUN_ROLE,
    date: "2026-07-06",
    modified: "2026-07-06",
    readTime: "5 min read",
    tags: ["andrologist", "men's health", "fertility", "sexual health"],
    primaryKeyword: "when to see an andrologist",
    secondaryKeywords: ["andrologist Krishnagiri", "men health specialist", "male reproductive health"],
    relatedService: "andrology-consultation",
    relatedServiceLabel: "Andrology Consultation",
    relatedArticles: ["male-infertility-causes", "erectile-dysfunction-causes", "male-hormonal-health"],
    sections: [
      {
        heading: "What an andrologist does",
        body: [
          "An andrologist specialises in male reproductive and sexual health, including fertility, sexual function and hormonal balance. It is a confidential, supportive space to discuss men's health concerns.",
        ],
      },
      {
        heading: "When to consider a consultation",
        body: ["See an andrologist if you experience any of these:"],
        list: [
          "Difficulty conceiving after trying for a year",
          "Persistent erectile dysfunction or sexual concerns",
          "Low libido or symptoms of hormonal imbalance",
          "Testicular pain, swelling or a lump",
          "Concerns about sperm or reproductive health",
        ],
      },
      {
        heading: "It is a common, worthwhile step",
        body: [
          "Many male health concerns are treatable, especially when addressed early. A consultation provides clarity and a clear plan.",
        ],
      },
      {
        heading: "A comfortable, confidential visit",
        body: [
          "An andrologist encourages open discussion in a private setting. You can ask questions, understand your health and decide on next steps together.",
        ],
      },
    ],
    faqs: [
      {
        q: "Do I need a referral to see an andrologist?",
        a: "In most cases you can book an andrology consultation directly without a referral.",
      },
      {
        q: "Is a consultation confidential?",
        a: "Yes. An andrology consultation is private and confidential by design.",
      },
    ],
    seo: {
      title: "When to See an Andrologist | AV Clinic",
      description:
        "Learn when men should see an andrologist in Krishnagiri — fertility, erectile function and hormonal health. Confidential care at AV Clinic.",
    },
  },
];